import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import TextInput from 'assets/components/inputs/text/TextInput';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { first, isEmpty } from 'lodash';
import ClaimTypeDocument from 'models/claims/claimTypeDocument/Model.ClaimTypeDocument';
import ClaimDocumentPreview from 'module/claims/views/claim/other/Claim.DocumentPreview';
import { useMemo } from 'react';
import { usePublicClaimSubmitProvider } from '../../Claim.Submit.Hooks';

function OneDocForm(props: { claimTypeDocument: ClaimTypeDocument }) {
  const { lang } = getLocales();
  const { form } = usePublicClaimSubmitProvider();

  const docForm = useForm<Utils.FormData<Model.IClaimJobDocument>>({
    default: {
      claimTypeDocumentId: props.claimTypeDocument.id,
      approved: !props.claimTypeDocument.approvalRequired,
    },
    onSubmit: async (data) => {
      const updatedClaimJobs = first(form.data?.claim?.claimJobs);
      updatedClaimJobs.claimJobDocuments = [...(updatedClaimJobs.claimJobDocuments ?? []), data];
      form.merge({ claim: { claimJobs: [updatedClaimJobs] } });
      docForm.reload();
    },
    validation: (data, errors) => {
      if (!data.name) errors.name = lang.required;
      if (!data.formFile)
        errors.formFile = {
          _objectError: lang.required,
        };
    },
  });

  const uploadedFile = useMemo(
    () =>
      !docForm.data?.formFile
        ? null
        : {
            original: docForm.data.formFile,
            type: docForm.data.formFile.type as Utils.File.MimeType,
            dataUrl: window.URL.createObjectURL(docForm.data.formFile),
            name: docForm.data.name,
            notes: docForm.data.notes,
          },
    [docForm.data.formFile]
  );

  const hasUploadedDocs = first(form.data.claim?.claimJobs)?.claimJobDocuments.some(
    (it) => it.claimTypeDocumentId === props.claimTypeDocument.id
  );
  const allowMoreUploads = !hasUploadedDocs ? true : !!props.claimTypeDocument.allowMultiple;

  return (
    <InputWrapper style={{ marginBottom: Theme.Size.L }}>
      <FileInputSimple
        name="file"
        value={uploadedFile}
        onChange={({ file }) => {
          docForm.update({
            formFile: file.original,
            name: file.original?.name,
          });
        }}
        label={props.claimTypeDocument.name}
        error={docForm.errors?.formFile?._objectError}
        disabled={!allowMoreUploads}
      />
      <TextInput
        name="name"
        label={lang.name}
        value={docForm.data?.name}
        error={docForm.errors?.name}
        onChange={docForm.update}
        disabled={!uploadedFile}
      />
      {props.claimTypeDocument?.allowNotes && (
        <TextInput
          name="notes"
          label={lang.notes}
          value={docForm.data?.notes}
          onChange={docForm.update}
          disabled={!uploadedFile}
        />
      )}
      <Button
        render={lang.add}
        onClick={docForm.submit}
        disabled={docForm.hasErrors}
        active={!docForm.hasErrors}
        class={ThemeButtonCircle}
        style={{ flexShrink: 0 }}
      />
    </InputWrapper>
  );
}
export default function ClaimSubmitClaimJobDocuments() {
  const { lang } = getLocales();
  const { form, uploadableDocuments } = usePublicClaimSubmitProvider();

  return !uploadableDocuments?.length ? null : (
    <div>
      <h2>{`${lang.claim} ${lang.documents}`}</h2>
      {!isEmpty(
        form?.data?.uploadableClaimTypeDocuments?.filter(
          (doc) =>
            doc.required &&
            !new Set(
              first(form?.data?.claim?.claimJobs)?.claimJobDocuments?.map((doc) => doc.claimTypeDocumentId)
            )?.has(doc.id)
        )
      ) &&
        uploadableDocuments.some(
          (doc) =>
            doc.required && !first(form.data.claim?.claimJobs)?.claimJobDocuments?.some((cjd) => cjd.id === doc.id)
        ) && (
          <p
            style={{
              backgroundColor: Theme.Color.error,
              color: 'white',
              padding: `${Theme.Size.M} ${Theme.Size.L}`,
              fontWeight: 'bold',
            }}
          >
            {`${lang.requiredFilesForTheFollowingDocs}: ${uploadableDocuments
              .filter(
                (doc) =>
                  doc.required &&
                  !first(form.data.claim?.claimJobs)?.claimJobDocuments?.some((cjd) => cjd.id === doc.id)
              )
              .map((doc) => doc.name)
              .join(', ')}`}
          </p>
        )}
      <br />
      <DataMap
        data={uploadableDocuments}
        render={({ data: doc }) => <OneDocForm key={doc.id} claimTypeDocument={doc} />}
      />
      <br />
      <ClaimDocumentPreview
        documents={first(form.data?.claim?.claimJobs)?.claimJobDocuments?.map((it, docIdx) => ({
          id: it.id,
          name: it.name,
          description: it.notes,
          documentType: lang.document,
          documentName: uploadableDocuments.find((ud) => ud.id === it.claimTypeDocumentId)?.name,
          onDelete: () => {
            const tempDocs = [...(first(form.data?.claim?.claimJobs)?.claimJobDocuments || [])];
            const newDocs = tempDocs?.filter((_, tempIdx) => docIdx !== tempIdx);
            const updatedClaimJobs = [...(form.data?.claim?.claimJobs || [])];
            first(updatedClaimJobs).claimJobDocuments = [...newDocs];
            form.merge({ claim: { claimJobs: updatedClaimJobs } });
          },
        }))}
      />
      <br />
    </div>
  );
}
